import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { Card, CommonCss, PageContainer, PageTitle } from 'styles/common.styles';
import { theme } from 'styles/theme';
import {
  Meta,
  Guide,
  ModeTabBar,
  ModeTab,
} from 'components/system/SystemPageChrome.styles';

import { ActionButtonGroup, CollapsibleSearchPanel, FormArea } from 'components/forms';
import { HiGridComponent, hiCol, useHiGridHook } from 'components/grid/hiGrid';
import {
  ActiveGridHeader,
  ActiveGridProvider,
  ActiveGridScope,
  useActiveGrid,
} from 'contexts/ActiveGridContext';
import {
  GridExportFocusScope,
  GridExportProvider,
  useExcelExportAction,
  useRegisterGridExport,
} from 'components/grid/excel';
import { showAlert } from 'utils/toastBridge';
import {
  ACCOUNT_STATUS_OPTIONS,
  enrichUserRows,
  filterUsers,
  getCachedUsers,
  loadUsers,
} from 'master/userAccountStore';
import { listDeptAuthPrograms } from 'master/deptAuthProgramCatalog';
import {
  EXCEPTION_TYPE_OPTIONS,
  buildExceptionButtonMatrix,
  loadExceptionActions,
  saveExceptionActions,
  validateExceptionActions,
} from 'master/permissionStore';

const USER_GRID_ID = 'exception-perm-user';
const PROGRAM_GRID_ID = 'exception-perm-program';
const BUTTON_GRID_ID = 'exception-perm-button';
const SEARCH_LABEL_WIDTH = '56px';

const Split = styled.div`
  display: flex;
  gap: 8px;
  flex: 1;
  min-height: 0;
`;

const Panel = styled(Card)`
  display: flex;
  flex: ${(p) => p.$flex || 1};
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  padding: 0;
  overflow: hidden;
`;

const RightStack = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  gap: 8px;
`;

const GridBody = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
`;



/**
 * 예외권한설정
 * - 좌측: 사용자
 * - 우측 상단: 프로그램 리스트 / 하단: 선택 프로그램의 버튼 컨트롤
 */
function ExceptionPermissionManageInner() {
  const { user, isMaster } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const userGridRef = useRef(null);
  const programGridRef = useRef(null);
  const buttonGridRef = useRef(null);
  const selectedAccountUidRef = useRef('');
  const selectedScreenIdRef = useRef('');
  const allActionsRef = useRef([]);
  const [selectedHint, setSelectedHint] = useState(
    '사용자를 선택하면 프로그램·버튼 예외를 설정할 수 있습니다.'
  );
  const [programHint, setProgramHint] = useState('프로그램을 선택하면 버튼 컨트롤을 설정합니다.');
  const { activeGridId } = useActiveGrid();

  const programCatalog = useMemo(() => listDeptAuthPrograms(), []);

  const searchDefaults = useMemo(
    () => ({ tenantCode, loginId: '', userNm: '', status: 'ACTIVE' }),
    [tenantCode]
  );
  const methods = useForm({ mode: 'onSubmit', defaultValues: searchDefaults });
  const { getValues, setValue } = methods;

  useEffect(() => {
    setValue('tenantCode', tenantCode);
  }, [setValue, tenantCode]);

  const mergeButtonRows = useCallback(() => {
    const accountUid = selectedAccountUidRef.current;
    const screenId = selectedScreenIdRef.current;
    if (!accountUid || !screenId) return;
    const current = buttonGridRef.current?.getData?.() || [];
    allActionsRef.current = [
      ...allActionsRef.current.filter(
        (r) => !(r.accountUid === accountUid && r.screenId === screenId)
      ),
      ...current
        .filter((r) => r.appliedYn === 'Y')
        .map((r) => ({ ...r, accountUid, screenId, mod: '' })),
    ];
  }, []);

  const loadButtonPanel = useCallback(
    (accountUid, screenId) => {
      if (!accountUid || !screenId) {
        buttonGridRef.current?.setData?.([]);
        return;
      }
      buttonGridRef.current?.setData?.(
        buildExceptionButtonMatrix(accountUid, screenId, allActionsRef.current)
      );
    },
    []
  );

  const loadProgramPanel = useCallback(
    (accountUid) => {
      if (!accountUid) {
        programGridRef.current?.setData?.([]);
        buttonGridRef.current?.setData?.([]);
        return;
      }
      const appliedScreens = new Set(
        allActionsRef.current
          .filter((r) => r.accountUid === accountUid && r.appliedYn === 'Y' && r.mod !== 'D')
          .map((r) => r.screenId)
      );
      programGridRef.current?.setData?.(
        programCatalog.map((p) => ({
          ...p,
          id: `${accountUid}|${p.screenId}`,
          accountUid,
          hasExceptionYn: appliedScreens.has(p.screenId) ? 'Y' : 'N',
          exceptionCount: allActionsRef.current.filter(
            (r) =>
              r.accountUid === accountUid &&
              r.screenId === p.screenId &&
              r.appliedYn === 'Y' &&
              r.mod !== 'D'
          ).length,
        }))
      );
    },
    [programCatalog]
  );

  const selectProgram = useCallback(
    (row) => {
      if (!row?.screenId || !selectedAccountUidRef.current) return;
      if (
        selectedScreenIdRef.current &&
        selectedScreenIdRef.current !== row.screenId
      ) {
        mergeButtonRows();
      }
      selectedScreenIdRef.current = row.screenId;
      setProgramHint(
        `${row.screenName || row.screenId} · 버튼 컨트롤 (적용 체크 시 예외로 저장)`
      );
      loadButtonPanel(selectedAccountUidRef.current, row.screenId);
    },
    [loadButtonPanel, mergeButtonRows]
  );

  const selectUser = useCallback(
    (row) => {
      if (!row?.accountUid) return;
      if (
        selectedAccountUidRef.current &&
        selectedAccountUidRef.current !== row.accountUid
      ) {
        mergeButtonRows();
      }
      selectedAccountUidRef.current = row.accountUid;
      selectedScreenIdRef.current = '';
      setSelectedHint(
        `${row.loginId || ''} · ${row.userNm || ''}${
          row.deptName ? ` · 주관 ${row.deptName}` : ''
        }`
      );
      setProgramHint('프로그램을 선택하면 버튼 컨트롤을 설정합니다.');
      loadProgramPanel(row.accountUid);
      buttonGridRef.current?.setData?.([]);
    },
    [loadProgramPanel, mergeButtonRows]
  );

  const handleSearch = useCallback(async () => {
    allActionsRef.current = await loadExceptionActions(tenantCode);
    const filtered = filterUsers(enrichUserRows(await loadUsers(tenantCode, user)), getValues());
    userGridRef.current?.setData?.(filtered);
    const selected = filtered.find((r) => r.accountUid === selectedAccountUidRef.current);
    if (selected) {
      selectedAccountUidRef.current = '';
      selectUser(selected);
      return;
    }
    selectedAccountUidRef.current = '';
    selectedScreenIdRef.current = '';
    setSelectedHint('사용자를 선택하면 프로그램·버튼 예외를 설정할 수 있습니다.');
    setProgramHint('프로그램을 선택하면 버튼 컨트롤을 설정합니다.');
    programGridRef.current?.setData?.([]);
    buttonGridRef.current?.setData?.([]);
  }, [getValues, selectUser, tenantCode, user]);

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userColumns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '사용자ID', field: 'loginId', size: 110 }),
      hiCol.roTextColumn({ header: '사용자명', field: 'userNm', size: 100 }),
      hiCol.roTextColumn({ header: '주관부서', field: 'deptName', size: 140 }),
      hiCol.roTextColumn({ header: '계정상태', field: 'statusNm', size: 90, align: 'center' }),
    ],
    []
  );

  const programColumns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '모듈', field: 'moduleName', size: 90 }),
      hiCol.roTextColumn({ header: '화면명', field: 'screenName', size: 160 }),
      hiCol.roTextColumn({ header: '화면ID', field: 'screenId', size: 140 }),
      hiCol.roTextColumn({ header: '유형', field: 'menuTypeNm', size: 70, align: 'center' }),
      hiCol.roTextColumn({
        header: '예외건수',
        field: 'exceptionCount',
        size: 80,
        align: 'center',
      }),
      hiCol.roTextColumn({
        header: '예외여부',
        field: 'hasExceptionYn',
        size: 80,
        align: 'center',
      }),
    ],
    []
  );

  const buttonColumns = useMemo(
    () => [
      hiCol.edCheckboxColumn({ header: '적용', field: 'appliedYn', size: 72 }),
      hiCol.roTextColumn({ header: '버튼', field: 'actionName', size: 100 }),
      hiCol.edComboColumn({
        header: '예외유형',
        field: 'exceptionType',
        size: 100,
        options: EXCEPTION_TYPE_OPTIONS,
      }),
      hiCol.edTextColumn({ header: '사유', field: 'reason', size: 220 }),
    ],
    []
  );

  const userGrid = useHiGridHook({
    gridRef: userGridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: false,
    confirmCheck: false,
    defaultRow: {},
  });

  const programGrid = useHiGridHook({
    gridRef: programGridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: false,
    confirmCheck: false,
    defaultRow: {},
  });

  const buttonGrid = useHiGridHook({
    gridRef: buttonGridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: false,
    defaultRow: {},
  });

  useEffect(() => {
    userGrid.onSelectRow = (row) => selectUser(row);
    userGrid.onRowClicked = ({ data }) => selectUser(data);
  }, [selectUser, userGrid]);

  useEffect(() => {
    programGrid.onSelectRow = (row) => selectProgram(row);
    programGrid.onRowClicked = ({ data }) => selectProgram(data);
  }, [programGrid, selectProgram]);

  useEffect(() => {
    buttonGrid.onAfterSaveCell = () => {
      mergeButtonRows();
      if (selectedAccountUidRef.current) {
        loadProgramPanel(selectedAccountUidRef.current);
      }
    };
  }, [buttonGrid, loadProgramPanel, mergeButtonRows]);

  const handleSave = useCallback(async () => {
    mergeButtonRows();
    const users = getCachedUsers(tenantCode);
    const rows = allActionsRef.current.filter((r) => r.mod !== 'D' && r.appliedYn === 'Y');
    const validation = validateExceptionActions(rows, users, programCatalog);
    if (!validation.ok) {
      showAlert(validation.message, 'warning');
      return;
    }
    try {
      allActionsRef.current = await saveExceptionActions(tenantCode, rows);
      if (selectedAccountUidRef.current) {
        loadProgramPanel(selectedAccountUidRef.current);
        if (selectedScreenIdRef.current) {
          loadButtonPanel(selectedAccountUidRef.current, selectedScreenIdRef.current);
        }
      }
      showAlert('예외권한(프로그램·버튼)을 저장했습니다.', 'success');
    } catch (e) {
      showAlert(e?.message || '저장에 실패했습니다.', 'error');
    }
  }, [
    loadButtonPanel,
    loadProgramPanel,
    mergeButtonRows,
    programCatalog,
    tenantCode,
    user,
  ]);

  const searchFields = useMemo(
    () => [
      [
        {
          name: 'loginId',
          label: '사용자ID',
          type: 'text',
          width: CommonCss.w100,
          labelWidth: SEARCH_LABEL_WIDTH,
        },
        {
          name: 'userNm',
          label: '사용자명',
          type: 'text',
          width: CommonCss.w100,
          labelWidth: SEARCH_LABEL_WIDTH,
        },
        {
          name: 'status',
          label: '계정상태',
          type: 'select',
          width: CommonCss.w100,
          labelWidth: SEARCH_LABEL_WIDTH,
          options: [{ value: '', label: '전체' }, ...ACCOUNT_STATUS_OPTIONS],
        },
      ],
    ],
    []
  );

  useRegisterGridExport({
    id: USER_GRID_ID,
    label: '사용자',
    getData: () =>
      userGridRef.current?.getFilteredData?.() ?? userGridRef.current?.getData?.() ?? [],
    getDataColumns: () => userGridRef.current?.getDataColumns?.() ?? [],
    joinKeys: ['accountUid'],
    order: 0,
  });
  useRegisterGridExport({
    id: PROGRAM_GRID_ID,
    label: '프로그램',
    getData: () =>
      programGridRef.current?.getFilteredData?.() ?? programGridRef.current?.getData?.() ?? [],
    getDataColumns: () => programGridRef.current?.getDataColumns?.() ?? [],
    joinKeys: ['screenId'],
    order: 1,
  });
  useRegisterGridExport({
    id: BUTTON_GRID_ID,
    label: '버튼컨트롤',
    getData: () =>
      buttonGridRef.current?.getFilteredData?.() ?? buttonGridRef.current?.getData?.() ?? [],
    getDataColumns: () => buttonGridRef.current?.getDataColumns?.() ?? [],
    joinKeys: ['actionCode'],
    order: 2,
  });

  const { openDialog: openExcelDialog, dialog: excelDialog } = useExcelExportAction({
    fileName: '예외권한설정',
  });

  const actionButtons = useMemo(
    () => [
      { id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7' },
      {
        id: 'save',
        label: '저장',
        icon: '✅',
        onClick: handleSave,
        hotkey: 'F10',
        variant: 'success',
      },
      { id: 'excel', label: '엑셀', icon: '📥', onClick: openExcelDialog, hotkey: 'F11' },
    ],
    [handleSave, handleSearch, openExcelDialog]
  );

  if (!isMaster) {
    return (
      <PageContainer>
        <PageTitle>예외권한설정</PageTitle>
        <Meta>기관 관리자 전용 화면입니다. 접근 권한이 없습니다.</Meta>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} />
        <CollapsibleSearchPanel
          storageKey="master-exception-permission"
          prefsUserId={prefsUserId}
          summary="사용자 조회"
        >
          <FormArea
            fields={searchFields}
            compact
            onFieldEnter={handleSearch}
            defaultValues={searchDefaults}
          />
        </CollapsibleSearchPanel>
      </FormProvider>

      <Meta>
        사용자별 프로그램·버튼 예외 · 제한(DENY) / 추가(GRANT)
        {' · '}부서·관리자 기본권한과 별도로 적용
      </Meta>

      <Split>
        <Panel $flex={1.1}>
          <ActiveGridHeader gridId={USER_GRID_ID} extra="조회 전용">
            사용자
          </ActiveGridHeader>
          <Meta style={{ padding: '4px 12px' }}>{selectedHint}</Meta>
          <GridBody>
            <ActiveGridScope
              gridId={USER_GRID_ID}
              label="사용자"
              order={0}
              canAdd={false}
              canDelete={false}
              style={{ flex: 1, minHeight: 0, height: '100%' }}
            >
              <GridExportFocusScope
                gridId={USER_GRID_ID}
                style={{ flex: 1, minHeight: 0, height: '100%' }}
              >
                <HiGridComponent
                  ref={userGridRef}
                  columns={userColumns}
                  height="100%"
                  showRowNumber
                  rowNumberSize={56}
                  headerActive={activeGridId === USER_GRID_ID}
                  enableColumnFilters
                  enableHeaderMenu
                  enableSelectionStats
                  enableFreeze
                  enableQuickExport
                  exportFileName="사용자"
                  gridId={USER_GRID_ID}
                  prefsUserId={prefsUserId}
                />
              </GridExportFocusScope>
            </ActiveGridScope>
          </GridBody>
        </Panel>

        <Panel $flex={1.45}>
          <RightStack>
            <Panel $flex={1.1} style={{ boxShadow: 'none', border: `1px solid ${theme.colors.border}` }}>
              <ActiveGridHeader gridId={PROGRAM_GRID_ID} extra="행 선택 → 하단 버튼 설정">
                프로그램
              </ActiveGridHeader>
              <GridBody>
                <ActiveGridScope
                  gridId={PROGRAM_GRID_ID}
                  label="프로그램"
                  order={1}
                  canAdd={false}
                  canDelete={false}
                  style={{ flex: 1, minHeight: 0, height: '100%' }}
                >
                  <GridExportFocusScope
                    gridId={PROGRAM_GRID_ID}
                    style={{ flex: 1, minHeight: 0, height: '100%' }}
                  >
                    <HiGridComponent
                      ref={programGridRef}
                      columns={programColumns}
                      height="100%"
                      showRowNumber
                      rowNumberSize={56}
                      headerActive={activeGridId === PROGRAM_GRID_ID}
                      enableColumnFilters
                      enableHeaderMenu
                      enableSelectionStats
                      enableFreeze
                      enableQuickExport
                      exportFileName="예외프로그램"
                      gridId={PROGRAM_GRID_ID}
                      prefsUserId={prefsUserId}
                    />
                  </GridExportFocusScope>
                </ActiveGridScope>
              </GridBody>
            </Panel>

            <Panel $flex={0.95} style={{ boxShadow: 'none', border: `1px solid ${theme.colors.border}` }}>
              <ActiveGridHeader gridId={BUTTON_GRID_ID} extra="적용 체크 · 예외유형 · F10 저장">
                버튼 컨트롤
              </ActiveGridHeader>
              <Meta style={{ padding: '4px 12px' }}>{programHint}</Meta>
              <GridBody>
                <ActiveGridScope
                  gridId={BUTTON_GRID_ID}
                  label="버튼컨트롤"
                  order={2}
                  canAdd={false}
                  canDelete={false}
                  style={{ flex: 1, minHeight: 0, height: '100%' }}
                >
                  <GridExportFocusScope
                    gridId={BUTTON_GRID_ID}
                    style={{ flex: 1, minHeight: 0, height: '100%' }}
                  >
                    <HiGridComponent
                      ref={buttonGridRef}
                      columns={buttonColumns}
                      height="100%"
                      showRowNumber
                      rowNumberSize={56}
                      headerActive={activeGridId === BUTTON_GRID_ID}
                      {...buttonGrid.contextMenuProps}
                      enableColumnFilters
                      enableHeaderMenu
                      enableSelectionStats
                      enableFreeze
                      enableQuickExport
                      exportFileName="예외버튼"
                      gridId={BUTTON_GRID_ID}
                      prefsUserId={prefsUserId}
                      colOptions={buttonGrid.colOptions}
                    />
                  </GridExportFocusScope>
                </ActiveGridScope>
              </GridBody>
            </Panel>

            <Guide>
              예: 전표등록에서 「삭제」만 제한(DENY)하거나, 감사 기간에 「엑셀」만 추가(GRANT).
              적용 체크된 버튼만 저장됩니다.
            </Guide>
          </RightStack>
        </Panel>
      </Split>

      {excelDialog}
    </PageContainer>
  );
}

export default function ExceptionPermissionManage() {
  return (
    <GridExportProvider>
      <ActiveGridProvider>
        <ExceptionPermissionManageInner />
      </ActiveGridProvider>
    </GridExportProvider>
  );
}
